import {
  Alert,
  Avatar,
  Box,
  Button,
  CircularProgress,
  Dialog,
  Drawer,
  Fab,
  IconButton,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Popover,
  Snackbar,
  Typography,
  useTheme,
} from "@mui/material";
import ApplicationHeader from "./ApplicationHeader";
import RoutesComponent from "../../routes";
import React, {
  useEffect,
  useState,
  useRef,
  useCallback,
  Suspense,
  lazy,
  memo,
  startTransition,
} from "react";
import { login_API } from "../../api/controller/authController";
import { useApplicationContext } from "../../context/ApplicationContext";
import useErrorHandler from "../../api/errorHandler/useErrorHandler";
import { HttpStatusCode } from "axios";
import {
  Navigate,
  Outlet,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from "react-router-dom";
import ProtectedRoute from "../../routes/ProtectedRoute";
import StoryContainer from "../userStory";
import RequirementsContainer from "../projects/ActiveProject";
import LoginContainer from "../auth/login";
import { getFolderStructureList_API } from "../../api/controller/RequirementController";
import {
  APP_CONFIG,
  AuthPages,
  CONSOLE_MODE,
  FolderType,
  ModulePath,
  Severity,
  USER_ROLE,
} from "../../constant/ApplicationVariables";
import ActiveProject from "../projects/ActiveProject";
import { font } from "../../styles/font";
import {
  AddTwoTone,
  DeleteTwoTone,
  EditTwoTone,
  HelpTwoTone,
  LogoutOutlined,
} from "@mui/icons-material";
import ApplicationSidebar from "./ApplicationSidebar";
import ChatWindow from "../projects/ChatWindow";
import {
  addModule_API,
  deleteModule_API,
  updateModule_API,
} from "../../api/controller/ModuleController";
import CreateModuleDialog from "../dialog/CreateModuleDialog";
import EditModuleDialog from "../dialog/EditModuleDialog";
import DeleteModuleDialog from "../dialog/DeleteModuleDialog";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import TokenExtendDialog from "../dialog/TokenExtendDialog";
import RegisterContainer from "../auth/register";
import ForgotPasswordContainer from "../auth/forgotPassword";
import VerifyAccountContainer from "../auth/verifyAccount";
import ResetPasswordContainer from "../auth/resetPassword";
import { getUserDetail_API } from "../../api/controller/userController";
import { Panel, PanelGroup, PanelResizeHandle } from "react-resizable-panels";
import { FaGripLinesVertical } from "react-icons/fa";

import { getLabelList_API } from "../../api/controller/SettingsController";
import TokenExpiredDialog from "../dialog/TokenExpiredDialog";
import FallbackUILoader from "../loader/FallbackUILoader";
import ApplicationMainContainer from "../container/ApplicationMainContainer";
import SuperAdminContainer from "../super-admin";
import ChooseLicenseDialog from "../dialog/license/ChooseLicenseDialog";
import { getLicensePlanSubscriptionDetail_API } from "../../api/controller/LicenseController";
import sessionManager from "../../api/useAxios/SessionManager";

/* -------------------------------------------------------------------------- */
/*                            Lazy Loaded Components                          */
/* -------------------------------------------------------------------------- */

const ProjectsContainer = lazy(() =>
  import("../projects").catch((error) => {
    console.error("Failed to load ProjectsContainer:", error);
    return { default: () => <div>Failed to load Projects</div> };
  })
);

const ProfileContainer = lazy(() =>
  import("../profile").catch((error) => {
    console.error("Failed to load ProfileContainer:", error);
    return { default: () => <div>Failed to load Profile</div> };
  })
);

const TaskContainer = lazy(() =>
  import("../tasks").catch((error) => {
    console.error("Failed to load TaskContainer:", error);
    return { default: () => <div>Failed to load Tasks</div> };
  })
);

const MyActivityContainer = lazy(() =>
  import("../myActvity").catch((error) => {
    console.error("Failed to load MyActivityContainer:", error);
    return { default: () => <div>Failed to load My Activity</div> };
  })
);

const ApplicationLayoutLeftSection = lazy(() =>
  import("./ApplicationLayoutLeftSection").catch((error) => {
    console.error("Failed to load ApplicationLayoutLeftSection:", error);
    return { default: () => <div>Failed to load Left Section</div> };
  })
);

const ApplicationLayoutRightSection = lazy(() =>
  import("./ApplicationLayoutRightSection").catch((error) => {
    console.error("Failed to load ApplicationLayoutRightSection:", error);
    return { default: () => <div>Failed to load Right Section</div> };
  })
);

const ApplicationLayoutMiddleSection = lazy(() =>
  import("./ApplicationLayoutMiddleSection").catch((error) => {
    console.error("Failed to load ApplicationLayoutMiddleSection:", error);
    return { default: () => <div>Failed to load Main Content</div> };
  })
);

const RegressionContainer = lazy(() =>
  import("../regression/pages/RegressionSuites").catch((error) => {
    console.error("Failed to load RegressionContainer:", error);
    return { default: () => <div>Failed to load Regression</div> };
  })
);

/* -------------------------------------------------------------------------- */
/*                              Error Boundary                                */
/* -------------------------------------------------------------------------- */

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error("ErrorBoundary caught an error:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <Box p={3} textAlign="center">
          <Typography color="error" variant="h6">
            Something went wrong
          </Typography>
          <Typography variant="body2" color="textSecondary">
            {this.state.error?.message || "Please try refreshing the page"}
          </Typography>
          <Button
            variant="contained"
            onClick={() => window.location.reload()}
            sx={{ mt: 2 }}
          >
            Refresh Page
          </Button>
        </Box>
      );
    }
    return this.props.children;
  }
}

/* -------------------------------------------------------------------------- */
/*                     Safe Route Wrapper (replaces CachedComponent)          */
/* -------------------------------------------------------------------------- */

const RouteComponentWrapper = memo(function RouteComponentWrapper({
  component: Component,
  componentProps = {},
}) {
  if (!Component) {
    console.error("RouteComponentWrapper: component is undefined");
    return <div>Component not available</div>;
  }
  return <Component {...componentProps} />;
});

/* -------------------------------------------------------------------------- */
/*                          Protected Route Config                            */
/* -------------------------------------------------------------------------- */

export const protectedRoutes = [
  {
    path: ModulePath.superAdmin,
    element: <RouteComponentWrapper component={ApplicationMainContainer} />,
  },
  {
    path: ModulePath.projects,
    element: <RouteComponentWrapper component={ApplicationMainContainer} />,
  },
  {
    path: ModulePath.programmes,
    element: <RouteComponentWrapper component={ApplicationMainContainer} />,
  },
  {
    path: ModulePath.organisations,
    element: <RouteComponentWrapper component={ApplicationMainContainer} />,
  },
  {
    path: ModulePath.marketplace,
    element: <RouteComponentWrapper component={ApplicationMainContainer} />,
  },
  {
    path: ModulePath.settings,
    element: <RouteComponentWrapper component={ApplicationMainContainer} />,
  },
  {
    path: ModulePath.requirements,
    element: <RouteComponentWrapper component={ApplicationMainContainer} />,
  },
  {
    path: ModulePath.useCase,
    element: <RouteComponentWrapper component={ApplicationMainContainer} />,
  },
  {
    path: ModulePath.testCase,
    element: <RouteComponentWrapper component={ApplicationMainContainer} />,
  },
  {
    path: ModulePath.dashboard,
    element: <RouteComponentWrapper component={ApplicationMainContainer} />,
  },
  {
    path: ModulePath.configuration,
    element: <RouteComponentWrapper component={ApplicationMainContainer} />,
  },
  {
    path: ModulePath.task,
    element: <RouteComponentWrapper component={ApplicationMainContainer} />,
  },
  {
    path: ModulePath.execution,
    element: <RouteComponentWrapper component={ApplicationMainContainer} />,
  },
  {
    path: ModulePath.planning,
    element: <RouteComponentWrapper component={ApplicationMainContainer} />,
  },
  {
    path: ModulePath.regression,
    element: <RouteComponentWrapper component={ApplicationMainContainer} />,
  },
];

/* -------------------------------------------------------------------------- */
/*                            Public Route Config                             */
/* -------------------------------------------------------------------------- */

export const publicRoutes = [
  { path: ModulePath.login, element: <LoginContainer /> },
  { path: ModulePath.register, element: <RegisterContainer /> },
  { path: ModulePath.forgotPassword, element: <ForgotPasswordContainer /> },
  { path: ModulePath.verifyAccount, element: <VerifyAccountContainer /> },
  { path: ModulePath.resetPassword, element: <ResetPasswordContainer /> },
];

/* -------------------------------------------------------------------------- */
/*                          Main Application Layout                           */
/* -------------------------------------------------------------------------- */

export default function ApplicationLayout() {
  const {
    globalSnackbar,
    setGLobalSnackBar,
    globalLoader,
    setGlobalLoader,
    userLicenseDetail,
    setUserLicenseDetail,
    handleSnackbar,
    profileDetails,
    setProfileDetails,
    showValidTillConfirmation,
    setShowValidTillConfirmation,
    showTokenExpired,
    setShowTokenExpired,
    userDetail,
    handleRedirectAfterAuthentication,
    showLicenseDialog,
    setShowLicenseDialog,
  } = useApplicationContext();

  const theme = useTheme();
  const errorHandler = useErrorHandler();
  const pathName = useLocation()?.pathname;
  const navigate = useNavigate();

  const handleGetLicenseDetail = useCallback(async () => {
    try {
      setGlobalLoader(true);
      const response = await getLicensePlanSubscriptionDetail_API();

      if (response && response.status) {
        errorHandler(response, setGlobalLoader);
      }

      if (response?.status === HttpStatusCode.Ok) {
        setUserLicenseDetail(response.data);
      }
    } catch (error) {
      console.error("License detail error:", error);
      handleSnackbar(error?.message || String(error), Severity.error);
    } finally {
      setTimeout(() => setGlobalLoader(false), 1000);
    }
  }, [setGlobalLoader, errorHandler, setUserLicenseDetail, handleSnackbar]);

  const handleCloseLicenseDialog = useCallback(() => {
    setShowLicenseDialog(false);
  }, [setShowLicenseDialog]);

  const handleConfirmLicenseDialog = useCallback(() => {
    handleCloseLicenseDialog();
    handleGetLicenseDetail();
  }, [handleCloseLicenseDialog, handleGetLicenseDetail]);

  // Super Admin page redirect
  useEffect(() => {
    if (!userDetail?.role) return;

    const isSuperAdmin =
      userDetail.role?.toLowerCase() === USER_ROLE.superadmin?.toLowerCase();
    const isOnSuperAdminPath = pathName === ModulePath.superAdmin;

    if (isSuperAdmin && !isOnSuperAdminPath) {
      handleRedirectAfterAuthentication(userDetail.role);
    } else if (!isSuperAdmin && isOnSuperAdminPath) {
      handleRedirectAfterAuthentication(userDetail.role);
    }
  }, [userDetail?.role, pathName, handleRedirectAfterAuthentication]);

  return (
    <ErrorBoundary>
      <Box className="w-full h-screen flex overflow-hidden relative">
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <Box className="w-full h-[100dvh] flex flex-col">
            <Suspense fallback={<FallbackUILoader />}>
              <Routes>
                <Route element={<ProtectedRoute />}>
                  <Route element={<ApplicationLayoutMainContainer />}>
                    {protectedRoutes.map((route) => (
                      <Route key={route.path} {...route} />
                    ))}
                  </Route>
                </Route>

                <Route
                  exact
                  path="/"
                  element={
                    <Navigate
                      to={
                        localStorage.getItem("access-token")
                          ? ModulePath.projects
                          : ModulePath.login
                      }
                      replace
                    />
                  }
                  key="home"
                />

                {publicRoutes.map((route) => (
                  <Route key={route.path} {...route} />
                ))}
              </Routes>
            </Suspense>
          </Box>
        </LocalizationProvider>

        {showValidTillConfirmation && <TokenExtendDialog />}
        {showTokenExpired && <TokenExpiredDialog />}

        {showLicenseDialog && (
          <Dialog
            open={showLicenseDialog}
            scroll="paper"
            maxWidth="lg"
            fullWidth
          >
            <ChooseLicenseDialog
              data={null}
              handleClose={handleCloseLicenseDialog}
              handleConfirm={handleConfirmLicenseDialog}
            />
          </Dialog>
        )}
      </Box>
    </ErrorBoundary>
  );
}

/* -------------------------------------------------------------------------- */
/*                    Application Layout Main Container                       */
/* -------------------------------------------------------------------------- */

function ApplicationLayoutMainContainer() {
  const {
    toggleChatWindow,
    setToggleChatWindow,
    toggleFolderSidebar,
    setToggleFolderSidebar,
    profileDetails,
    globalLoader,
    setGlobalLoader,
    handleSnackbar,
    userDetail,
    setUserDetail,
    userLicenseDetail,
    setUserLicenseDetail,
    showLicenseDialog,
    setShowLicenseDialog,
    activeProject,
    labelList,
    setLabelList,
    sidePanelSize,
    setSidePanelSize,
    AIPanelSize,
    setAIPanelSize,
    isAIWindowMaximized,
    setIsAIWindowMaximized,
    setShowValidTillConfirmation,
    handleLogout,
  } = useApplicationContext();

  const theme = useTheme();
  const pathName = useLocation()?.pathname;
  const errorHandler = useErrorHandler();

  // Refs to prevent duplicate API calls
  const userFetchedRef = useRef(false);
  const licenseFetchedRef = useRef(false);
  const labelsFetchedRef = useRef(false);
  const [isUserLoading, setIsUserLoading] = useState(false);
  const [isLicenseLoading, setIsLicenseLoading] = useState(false);

  // ===== Panel size constants (percentages) =====
  const DEFAULT_LEFT_SIZE = 18;      // was 30, too wide — adjusted to reasonable default
  const DEFAULT_RIGHT_SIZE = 25;
  const COLLAPSED_SIZE = 5;
  // Minimum expanded size — sidebar panel cannot go below this when expanded.
  // 15% of viewport is a safe floor. Increase to taste.
  const MIN_EXPANDED_SIZE = 12;
  // Maximum expanded size — user cannot drag beyond this
  const MAX_EXPANDED_SIZE = 35;

  const previousLeftPanelSize = useRef(DEFAULT_LEFT_SIZE);
  const [leftPanelKey, setLeftPanelKey] = useState(0);
  const [isLeftPanelReady, setIsLeftPanelReady] = useState(false);
  const [isRightPanelReady, setIsRightPanelReady] = useState(false);

  useEffect(() => {
    if (profileDetails && !AuthPages.includes(pathName)) {
      console.log("Initializing SessionManager");

      sessionManager.init({
        onSessionExpired: () => {
          console.log("Session expired callback");
          handleSnackbar("Session expired due to inactivity", Severity.warning);
          handleLogout();
        },
        onShowDialog: () => {
          console.log("Show session dialog");
          setShowValidTillConfirmation(true);
        },
        onHideDialog: () => {
          console.log("Hide session dialog");
          setShowValidTillConfirmation(false);
        },
        onTokenExpiring: (remainingSeconds) => {
          console.log(`Token expiring in ${remainingSeconds} seconds`);
        },
      });
    }

    return () => {
      if (profileDetails) {
        sessionManager.destroy();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profileDetails, pathName]);

  const shouldShowLeftPanel = useCallback(() => {
    const hiddenPaths = [
      ModulePath.projects,
      ModulePath.programmes,
      ModulePath.organisations,
      ModulePath.profile,
      ModulePath.myActivity,
      ModulePath.regression,
      ModulePath.task,
    ];
    return (
      !hiddenPaths.includes(pathName) &&
      !pathName.startsWith(ModulePath.marketplace)
    );
  }, [pathName]);

  const shouldShowRightPanel = useCallback(() => {
    const isSuperAdmin =
      userDetail?.role?.toLowerCase() === USER_ROLE.superadmin?.toLowerCase();

    if (isSuperAdmin) {
      return false;
    }

    const showLeft = shouldShowLeftPanel();
    return (
      showLeft ||
      pathName === ModulePath.task ||
      pathName === ModulePath.regression
    );
  }, [pathName, shouldShowLeftPanel, userDetail]);

  const shouldHideHeader = useCallback(() => {
  const headerHiddenPaths = [
    ModulePath.marketplace,
    ModulePath.superAdmin,
  ];
  return headerHiddenPaths.some((p) => pathName?.startsWith(p));
}, [pathName]);

  // Sync toggleFolderSidebar ↔ sidePanelSize
  useEffect(() => {
    if (toggleFolderSidebar) {
      if (previousLeftPanelSize.current > COLLAPSED_SIZE) {
        setSidePanelSize(previousLeftPanelSize.current);
        setLeftPanelKey((prev) => prev + 1);
      }
    } else {
      if (sidePanelSize > COLLAPSED_SIZE) {
        previousLeftPanelSize.current = sidePanelSize;
      }
      setSidePanelSize(COLLAPSED_SIZE);
      setLeftPanelKey((prev) => prev + 1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [toggleFolderSidebar]);

  // ===== Left panel resize — STRICT minimum enforcement =====
  const handleLeftResize = useCallback(
    (size) => {
      startTransition(() => {
        // When expanded, clamp between MIN_EXPANDED_SIZE and MAX_EXPANDED_SIZE
        if (toggleFolderSidebar) {
          let clamped = size;
          if (clamped < MIN_EXPANDED_SIZE) clamped = MIN_EXPANDED_SIZE;
          if (clamped > MAX_EXPANDED_SIZE) clamped = MAX_EXPANDED_SIZE;
          previousLeftPanelSize.current = clamped;
          setSidePanelSize(clamped);
        } else {
          // When collapsed, force collapsed size (cannot expand by dragging)
          setSidePanelSize(COLLAPSED_SIZE);
        }
      });
    },
    [toggleFolderSidebar, setSidePanelSize]
  );

  const handleRightResize = useCallback(
    (size) => {
      startTransition(() => {
        setAIPanelSize(
          Math.max(size, toggleChatWindow ? MIN_EXPANDED_SIZE : COLLAPSED_SIZE)
        );
      });
    },
    [toggleChatWindow, setAIPanelSize]
  );

  const handleGetUserDetail = useCallback(async () => {
    if (isUserLoading || userFetchedRef.current) return;

    try {
      setIsUserLoading(true);
      setGlobalLoader(true);
      const response = await getUserDetail_API();

      if (response?.status === HttpStatusCode.Ok) {
        setUserDetail(response.data);
        userFetchedRef.current = true;
      } else if (response) {
        errorHandler(response, setGlobalLoader);
      }
    } catch (error) {
      console.error("User detail error:", error);
      handleSnackbar(error?.message || String(error), Severity.error);
    } finally {
      setTimeout(() => {
        setGlobalLoader(false);
        setIsUserLoading(false);
      }, 1000);
    }
  }, [
    setGlobalLoader,
    errorHandler,
    setUserDetail,
    handleSnackbar,
    isUserLoading,
  ]);

  const handleGetLicenseDetail = useCallback(async () => {
    if (isLicenseLoading || licenseFetchedRef.current) return;

    try {
      setIsLicenseLoading(true);
      setGlobalLoader(true);
      const response = await getLicensePlanSubscriptionDetail_API();

      if (response?.status === HttpStatusCode.Ok) {
        setUserLicenseDetail(response.data);
        licenseFetchedRef.current = true;
      } else if (response) {
        errorHandler(response, setGlobalLoader);
      }
    } catch (error) {
      console.error("License detail error:", error);
      handleSnackbar(error?.message || String(error), Severity.error);
    } finally {
      setTimeout(() => {
        setGlobalLoader(false);
        setIsLicenseLoading(false);
      }, 1000);
    }
  }, [
    setGlobalLoader,
    errorHandler,
    setUserLicenseDetail,
    handleSnackbar,
    isLicenseLoading,
  ]);

  const handleGetLabelsList = useCallback(async () => {
    if (labelsFetchedRef.current) return;
    if (!activeProject?.name || pathName === ModulePath.projects) return;

    try {
      labelsFetchedRef.current = true;
      setGlobalLoader(true);
      const body = { name: activeProject.name };
      const query = new URLSearchParams(body).toString();
      const response = await getLabelList_API(query);

      if (response?.status === HttpStatusCode.Ok) {
        setLabelList(response.data);
      } else if (response) {
        errorHandler(response, setGlobalLoader);
      }
    } catch (error) {
      console.error("Labels list error:", error);
      handleSnackbar(error?.message || String(error), Severity.error);
    } finally {
      setTimeout(() => setGlobalLoader(false), 1000);
    }
  }, [
    activeProject?.name,
    pathName,
    setGlobalLoader,
    errorHandler,
    setLabelList,
    handleSnackbar,
  ]);

  useEffect(() => {
    if (
      profileDetails &&
      (!userDetail || profileDetails?.role !== userDetail?.role) &&
      !AuthPages.includes(pathName)
    ) {
      if (!userFetchedRef.current && !isUserLoading) {
        handleGetUserDetail();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profileDetails, userDetail, pathName]);

  useEffect(() => {
    if (
      profileDetails &&
      userDetail &&
      userDetail?.role !== USER_ROLE.superadmin
    ) {
      if (!licenseFetchedRef.current && !isLicenseLoading) {
        handleGetLicenseDetail();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profileDetails, userDetail]);

  useEffect(() => {
    if (activeProject?.name && pathName !== ModulePath.projects) {
      if (!labelsFetchedRef.current) {
        handleGetLabelsList();
      }
    } else {
      labelsFetchedRef.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeProject?.name, pathName]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLeftPanelReady(true);
      setIsRightPanelReady(true);
    }, 100);

    return () => clearTimeout(timer);
  }, []);

  const showLeft = shouldShowLeftPanel();
  const showRight = shouldShowRightPanel();

  // ===== Compute panel sizes =====
  const leftPanelDefaultSize = toggleFolderSidebar
    ? Math.max(
        Math.min(
          sidePanelSize > COLLAPSED_SIZE
            ? sidePanelSize
            : previousLeftPanelSize.current,
          MAX_EXPANDED_SIZE
        ),
        MIN_EXPANDED_SIZE
      )
    : COLLAPSED_SIZE;

  const leftPanelMinSize = toggleFolderSidebar
    ? MIN_EXPANDED_SIZE
    : COLLAPSED_SIZE;

  const leftPanelMaxSize = toggleFolderSidebar
    ? MAX_EXPANDED_SIZE
    : COLLAPSED_SIZE;

  const panelSizes = {
    left: {
      default: leftPanelDefaultSize,
      min: leftPanelMinSize,
      max: leftPanelMaxSize,
      // IMPORTANT: collapsible MUST be false so user cannot drag it to 0
      collapsible: false,
    },
    right: {
      default: COLLAPSED_SIZE,
      min: COLLAPSED_SIZE,
      max: COLLAPSED_SIZE,
      collapsible: false,
    },
  };

  return (
    <Box
      className="h-screen w-screen flex flex-col"
      bgcolor={theme.palette.background.custom}
    >
     {!shouldHideHeader() && (
  <Box
    className="h-[8vh] w-full flex px-0 py-0"
    bgcolor={theme.palette.background.default}
  >
    <ApplicationHeader />
  </Box>
)}

      <Box className="h-[92vh] w-full flex gap-0 px-0 py-0 relative">
        <PanelGroup
          autoSaveId="app-layout"
          direction="horizontal"
          style={{ width: "100%", height: "100%" }}
        >
          {/* Left Panel */}
          {showLeft && (
            <>
              <Panel
                key={`left-panel-${leftPanelKey}`}
                order={1}
                defaultSize={panelSizes.left.default}
                minSize={panelSizes.left.min}
                maxSize={panelSizes.left.max}
                collapsible={panelSizes.left.collapsible}
                onResize={handleLeftResize}
              >
                <Box
                  sx={{
                    height: "100%",
                    bgcolor: "background.paper",
                    overflow: "auto",
                  }}
                >
                  <Suspense fallback={<FallbackUILoader />}>
                    {isLeftPanelReady && <ApplicationLayoutLeftSection />}
                  </Suspense>
                </Box>
              </Panel>
              <CustomPanelResizeHandle />
            </>
          )}

          {/* Main Content */}
          <Panel order={showLeft ? 2 : 1} minSize={30}>
            <Suspense fallback={<FallbackUILoader />}>
              <ApplicationLayoutMiddleSection shouldShowLeftPanel={showLeft} />
            </Suspense>
          </Panel>

          {/* Right Panel - Hidden for super-admin */}
          {showRight && (
            <>
              <CustomPanelResizeHandle />
              <Panel
                order={showLeft ? 3 : 2}
                defaultSize={panelSizes.right.default}
                minSize={panelSizes.right.min}
                maxSize={panelSizes.right.max}
                collapsible={panelSizes.right.collapsible}
                onResize={handleRightResize}
              >
                <Box
                  sx={{
                    height: "100%",
                    bgcolor: "ai.light",
                    overflow: "auto",
                  }}
                >
                  <Suspense fallback={<FallbackUILoader />}>
                    {isRightPanelReady && <ApplicationLayoutRightSection />}
                  </Suspense>
                </Box>
              </Panel>
            </>
          )}
        </PanelGroup>
      </Box>
    </Box>
  );
}

/* -------------------------------------------------------------------------- */
/*                             Snackbar Component                             */
/* -------------------------------------------------------------------------- */

const SnackbarApplication = memo(({ globalSnackbar, setGLobalSnackBar }) => {
  const handleClose = useCallback(() => {
    setGLobalSnackBar({
      open: false,
      title: "",
      severity: "",
    });
  }, [setGLobalSnackBar]);

  return (
    <Snackbar
      autoHideDuration={3000}
      sx={{ zIndex: 99999, userSelect: "none" }}
      onClose={handleClose}
      anchorOrigin={{ horizontal: "left", vertical: "bottom" }}
      open={globalSnackbar?.open || false}
    >
      <Alert
        severity={globalSnackbar?.severity || "info"}
        variant="filled"
        sx={{ width: "100%", userSelect: "none" }}
        onClose={handleClose}
      >
        {globalSnackbar?.title || ""}
      </Alert>
    </Snackbar>
  );
});

/* -------------------------------------------------------------------------- */
/*                             Global Loader                                  */
/* -------------------------------------------------------------------------- */

const GlobalLoader = memo(() => {
  return (
    <Box
      className="absolute top-0 left-0 right-0 bottom-0 flex flex-col items-center justify-center w-full h-full z-20 backdrop-blur-0"
      sx={{
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "rgba(255, 255, 255, 0.7)",
        zIndex: 9998,
      }}
    >
      <CircularProgress />
      <Typography sx={{ mt: 2 }}>Loading...</Typography>
    </Box>
  );
});

/* -------------------------------------------------------------------------- */
/*                        Custom Panel Resize Handle                          */
/* -------------------------------------------------------------------------- */

const CustomPanelResizeHandle = memo(() => {
  const theme = useTheme();

  return (
    <PanelResizeHandle
      className="w-0.5 flex justify-center hover:bg-gray-200 transition-all ease-in-out duration-300"
      style={{ borderRadius: theme.shape.borderRadius }}
    >
      <Box className="w-0 h-full flex flex-col items-center justify-center">
        <FaGripLinesVertical size={12} />
      </Box>
    </PanelResizeHandle>
  );
});